import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface AdminDocument {
    id: number;
    title: string;
    description: string | null;
    category: string | null;
    file_type: string | null;
    file_url: string;
    is_active: boolean | number;
    display_order: number;
    created_at?: string;
    updated_at?: string;
}

// GET All Documents
export const useAdminDocuments = () => {
    return useQuery<AdminDocument[]>({
        queryKey: ["admin-documents"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/documents", { withCredentials: true });
            return data;
        },
    });
};

// GET Single Document
export const useAdminDocument = (id: number | null) => {
    return useQuery<AdminDocument>({
        queryKey: ["admin-document", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/documents/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE Document
export const useCreateDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData) => {
            const { data } = await axiosClient.post("/documents", payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
        },
    });
};

// UPDATE Document
export const useUpdateDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: FormData }) => {
            // Route update documents adalah POST (bukan PUT) - JANGAN pakai _method=PUT,
            // nanti Laravel mencari route PUT yang tidak terdaftar ("Method Not Allowed").
            const { data } = await axiosClient.post(`/documents/${id}`, payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
            queryClient.invalidateQueries({ queryKey: ["admin-document"] });
        },
    });
};

// DELETE Document
export const useDeleteDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/documents/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
        },
    });
};
